if (!customElements.get('scroll-shadow')) {
  const updaters = new WeakMap();
  class Updater {
    constructor(targetElement) {
      const update = this.update.bind(this, targetElement, getComputedStyle(targetElement));
      this.handleScroll = this.throttle(update);
      this.resizeObserver = new ResizeObserver(update);
    }

    clamp(num, min = 0, max) {
      if (num < min) return min
      if (num > max) return max
      return num
    }
    
    throttle(callback) {
      let id = null;
      return () => {
        if (id) return;
        id = requestAnimationFrame(() => {
          callback();
          id = null;
        })
      }
    }
  
    start(element, rootElement) {
      if (this.element) this.stop();
      if (element) {
        element.addEventListener('scroll', this.handleScroll);
        this.resizeObserver.observe(element);
        this.element = element;
      }
      if (rootElement) {
        this.resizeObserver.observe(rootElement);
        this.rootElement = rootElement;
      }
    }
  
    stop() {
      if (!this.element) return;
      this.element.removeEventListener('scroll', this.handleScroll);
      this.resizeObserver.disconnect();
      this.element = null;
      this.rootElement = null;
    }
  
    update(targetElement, computedStyle) {
      const { element, rootElement } = this;
      if (!element) return;
      const maxSize = Number(computedStyle.getPropertyValue('--scroll-shadow-size') || 14);
      const style = {
        '--top': this.clamp(element.scrollTop, 0, maxSize),
        '--bottom': this.clamp(element.scrollHeight - element.offsetHeight - element.scrollTop, 0, maxSize),
        '--left': this.clamp(element.scrollLeft, 0, maxSize),
        '--right': this.clamp(element.scrollWidth - element.offsetWidth - element.scrollLeft, 0, maxSize),
      }
  
      if (rootElement) {
        const clientRect = element.getBoundingClientRect();
        const rootClientRect = rootElement.getBoundingClientRect();
        Object.assign(style, {
          top: this.clamp(clientRect.top - rootClientRect.top),
          bottom: this.clamp(rootClientRect.bottom - clientRect.bottom),
          left: this.clamp(clientRect.left - rootClientRect.left),
          right: this.clamp(rootClientRect.right - clientRect.right),
        });
      }
  
      for (const key in style) {
        targetElement.style.setProperty(key, `${style[key]}px`);
      }
    }
  }
  customElements.define(
    'scroll-shadow',
    class ScrollShadow extends HTMLElement {
      constructor() {
        super();
        this.template = `
          <style>
          :host {
            display: inline-block;
            position: relative;
          }
          :host([hidden]) {
            display: none;
          }
          s {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            pointer-events: none;
            z-index: var(--z-index-temporary, 5);
            background:
              var(--scroll-shadow-top, radial-gradient(farthest-side at 50% 0%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) top/100% var(--top),
              var(--scroll-shadow-bottom, radial-gradient(farthest-side at 50% 100%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) bottom/100% var(--bottom),
              var(--scroll-shadow-left, radial-gradient(farthest-side at 0%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) left/var(--left) 100%,
              var(--scroll-shadow-right, radial-gradient(farthest-side at 100%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) right/var(--right) 100%;
            background-repeat: no-repeat;
          }
          </style>
          <slot></slot>
          <s></s>
        `
        this.attachShadow({ mode: 'open' }).innerHTML = this.template;
        updaters.set(this, new Updater(this.shadowRoot.lastElementChild));
      }

      static get observedAttributes() {
        return ['element']
      }

      get element() {
        return this.getAttribute('element')
      }
    
      set element(value) {
        this.setAttribute('element', value)
      }

      connectedCallback() {
        this.shadowRoot.querySelector('slot').addEventListener('slotchange', () => this.start());
        this.start();
      }
    
      disconnectedCallback() {
        updaters.get(this).stop();
      }

      attributeChangedCallback(_name, oldValue, newValue) {
        if (oldValue !== newValue) {
          this.scrollElement = newValue ? this.querySelector(newValue) : null;
          this.start();
        }
      }

      start() {
        const element = this.scrollElement || this.firstElementChild;
        updaters.get(this).start(element, this.scrollElement ? this.firstElementChild : null);
      }
    }
  );
}